import type { Dispatch, SetStateAction } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import type {
  IntakeScopeCandidate,
  IntakeState,
  IntakeWorkItem,
} from "../structured-intake-model.js";

interface Options {
  setClassificationBusy: Dispatch<SetStateAction<string | null>>;
  setError: Dispatch<SetStateAction<string>>;
  setIntakeActionBusy: Dispatch<SetStateAction<string | null>>;
  setIntakeState: Dispatch<SetStateAction<IntakeState | null>>;
}

export function useStructuredIntakeActions({
  setClassificationBusy,
  setError,
  setIntakeActionBusy,
  setIntakeState,
}: Options) {
  const refresh = async () => {
    setIntakeState(await apiFetch<IntakeState>("/v2/quote/intake"));
  };

  const confirmClassification = async (workItem: IntakeWorkItem, projectType: string) => {
    setClassificationBusy(workItem.id);
    setError("");
    try {
      const result = await apiFetch<IntakeState>("/v2/quote/intake/classifications/confirm", {
        method: "POST",
        body: JSON.stringify({ workItemId: workItem.id, projectType }),
      });
      setIntakeState(result);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "The project type could not be confirmed",
      );
    } finally {
      setClassificationBusy(null);
    }
  };

  const resolveScope = async (
    candidate: IntakeScopeCandidate,
    resolution: "accept" | "reject" | "correct",
    projectType?: string,
  ) => {
    setClassificationBusy(candidate.id);
    setError("");
    try {
      await apiFetch("/v2/quote/intake/scopes/resolve", {
        method: "POST",
        body: JSON.stringify({ candidateId: candidate.id, resolution, projectType }),
      });
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The project scope could not be updated");
    } finally {
      setClassificationBusy(null);
    }
  };

  const runDecision = async (id: string, path: string, failureMessage: string) => {
    setIntakeActionBusy(id);
    setError("");
    try {
      await apiFetch(path, { method: "POST" });
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : failureMessage);
    } finally {
      setIntakeActionBusy(null);
    }
  };

  const decideFact = (factId: string, decision: "confirm" | "reject") =>
    runDecision(
      factId,
      `/v2/quote/intake/facts/${factId}/${decision}`,
      "The project detail could not be updated",
    );

  const confirmMeasurement = (measurementId: string) =>
    runDecision(
      measurementId,
      `/v2/quote/intake/measurements/${measurementId}/confirm`,
      "The measurement could not be confirmed",
    );

  const decideObservation = (observationId: string, decision: "confirm" | "reject") =>
    runDecision(
      observationId,
      `/v2/quote/intake/observations/${observationId}/${decision}`,
      "The photo observation could not be updated",
    );

  const acceptStandardDimension = (candidateId: string) =>
    runDecision(
      candidateId,
      `/v2/quote/intake/standard-dimension-candidates/${candidateId}/accept`,
      "The standard-size approximation could not be accepted",
    );

  const uploadCapture = async (
    task: NonNullable<IntakeState["captureTasks"]>[number],
    file: File,
  ) => {
    setIntakeActionBusy(task.id);
    setError("");
    try {
      const captureType =
        task.capture_type === "marker_measurement"
          ? "marker_measurement_photo"
          : task.capture_type === "manual_measurement"
            ? "manual_measurement_photo"
            : task.capture_type;
      const result = await apiFetch<{
        imageId: string;
        upload: { url: string; fields: Record<string, string> };
      }>("/v1/quote/images/upload-policy", {
        method: "POST",
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          sizeBytes: file.size,
          captureTaskId: task.id,
          entityId: task.entity_id || undefined,
          captureType,
        }),
      });
      if (!result.upload.url.includes("/v1/local-uploads/")) {
        const form = new FormData();
        for (const [key, value] of Object.entries(result.upload.fields)) form.append(key, value);
        form.append("file", file);
        const response = await fetch(result.upload.url, { method: "POST", body: form });
        if (!response.ok) throw new Error("The private capture upload failed");
      }
      await apiFetch(`/v1/quote/images/${result.imageId}/complete`, { method: "POST" });
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The capture could not be uploaded");
    } finally {
      setIntakeActionBusy(null);
    }
  };

  return {
    acceptStandardDimension,
    confirmClassification,
    confirmMeasurement,
    resolveScope,
    decideFact,
    decideObservation,
    uploadCapture,
  };
}
